import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { WMTSSettings } from '../../explore-map.model';
import { ExploreMapService } from '../../explore-map.service';
import TileLayer from 'ol/layer/Tile';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
@Component({
  selector: 'app-cadastral-layer-panel',
  templateUrl: './cadastral-layer-panel.component.html',
  styleUrls: ['./cadastral-layer-panel.component.scss'],
})
export class CadastralLayerPanelComponent implements OnInit {
  @Input() map: any;
  onDestroy$ = new Subject();

  cadastralDatasets = [
    {
      displayName: 'Basemap Layer',
      layerName: 'background_greyscale',
      visible: true,
      loaded: true,
      minZoom: 3,
      maxZoom: 21,
      zIndex: '-2',
    },
    {
      displayName: 'All Cadastral Layers',
      layerName: 'dataset-layer',
      visible: true,
      loaded: true,
      minZoom: 3,
      maxZoom: 21,
      zIndex: '-1',
    },
    {
      displayName: 'Address',
      layerName: 'cadastral_address',
      visible: false,
      loaded: false,
      minZoom: 19,
      maxZoom: 21,
      zIndex: '5',
    },
    {
      displayName: 'Administrative Unit',
      layerName: 'cadastral_administrativeunit',
      visible: false,
      loaded: false,
      minZoom: 3,
      maxZoom: 21,
      zIndex: '-1',
    },
    {
      displayName: 'Building',
      layerName: 'cadastral_building',
      visible: false,
      loaded: false,
      minZoom: 15,
      maxZoom: 21,
      zIndex: '3',
    },
    {
      displayName: 'Building Part',
      layerName: 'cadastral_buildingpart',
      visible: false,
      loaded: false,
      minZoom: 17,
      maxZoom: 21,
      zIndex: '4',
    },
    {
      displayName: 'Cadastral Parcel',
      layerName: 'cadastral_cadastralparcel',
      visible: false,
      loaded: false,
      minZoom: 15,
      maxZoom: 21,
      zIndex: '2',
    },
    {
      displayName: 'Cadastral Zoning',
      layerName: 'cadastral_cadastralzoning',
      visible: false,
      loaded: false,
      minZoom: 14,
      maxZoom: 21,
      zIndex: '1',
    },
  ];

  constructor(
    private exploreMapService: ExploreMapService,
    private activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.activatedRoute.paramMap.pipe(takeUntil(this.onDestroy$)).subscribe((params) => {
      if (params.get('mapServiceSlug') !== 'cadastral-all') {
        this.setCadastralSublayersInvisible();
        this.setVisibility('dataset-layer', true);
      }
    });
  }

  layerInZoomRange(layer: any): boolean {
    if (this.map) {
      let view = this.map.getView();
      let currentZoom = view.getZoom();
      return Math.round(currentZoom) > layer.maxZoom || Math.round(currentZoom) < layer.minZoom  ? false : true ;
    } else {
      return true;
    }
  }

  onCheckboxChange(cadastralLayer): void {
    if (cadastralLayer.layerName === 'dataset-layer' && !cadastralLayer.visible) {
      /// When checking Cadastral All, uncheck all others
      this.setCadastralSublayersInvisible();
    } else if (cadastralLayer.layerName === 'background_greyscale' && !cadastralLayer.visible) {
      this.map.getLayers().forEach((layer) => {
        if (
          layer &&
          (layer.get('name') === 'background-layer' ||
            layer.get('name') === 'gazetteer-background-labels')
        ) {
          if (cadastralLayer.visible) {
            layer.setVisible(true);
          } else {
            layer.setVisible(false);
          }
        }
      });
    } else if (!cadastralLayer.visible) {
      // When checking an individual layer, uncheck Cadastral All
      this.cadastralDatasets.find((dataset) => dataset.layerName === 'dataset-layer').visible =
        false;
      this.setVisibility('dataset-layer', false);
    }
    cadastralLayer.visible = !cadastralLayer.visible;

    // Toggle background basemap layer
    if (cadastralLayer.layerName === 'background_greyscale') {
      this.setVisibility(cadastralLayer.layerName, cadastralLayer.visible);
      this.map.getLayers().forEach((layer) => {
        if (
          layer &&
          (layer.get('name') === 'background-layer' ||
            layer.get('name') === 'gazetteer-background-labels')
        ) {
          if (cadastralLayer.visible) {
            layer.setVisible(true);
          } else {
            layer.setVisible(false);
          }
        }
      });
    }
    if (!cadastralLayer.loaded) {
      this.addCadastralLayer(cadastralLayer);
    } else {
      this.setVisibility(cadastralLayer.layerName, cadastralLayer.visible);
    }
    let visibleLayer = this.cadastralDatasets.map(layer => { return layer.visible? layer.layerName : null});
    visibleLayer = visibleLayer.map(layer => layer === "dataset-layer" ? "cadastral_all" : layer)
    visibleLayer = visibleLayer.filter(x => x !== null);
    this.exploreMapService.changeWMSLayer(visibleLayer)
  }

  setCadastralSublayersInvisible(): void {
    this.cadastralDatasets.map((dataset) => {
      if (dataset.layerName !== 'dataset-layer' && dataset.layerName !== 'background_greyscale' && dataset.loaded && dataset.visible) {
        dataset.visible = false;
        this.setVisibility(dataset.layerName, false);
      }
    });
  }

  setVisibility(layerName: string, flag: boolean): void {
    this.map.getLayers().forEach((layer) => {
      if (layer && layer.get('name') === layerName) {
        layer.setVisible(flag);
      }
    });
  }

  addCadastralLayer(layer: any): void {
    const newTileLayer = new TileLayer({
      source: null,
      properties: {
        name: layer.layerName,
      },
    });
    const wmtsSettings = new WMTSSettings();
    wmtsSettings.layerName = layer.layerName;
    wmtsSettings.url = `maps/wmts/${wmtsSettings.layerName}/{TileMatrixSet}/{TileMatrix}/{TileCol}/{TileRow}.png?token=${environment.elsToken}`;

    let wmtsSource = this.exploreMapService.getWMTSSource(wmtsSettings);

    layer.loaded = true;

    this.map.addLayer(newTileLayer);
    newTileLayer.setSource(wmtsSource);
    newTileLayer.setZIndex(layer.zIndex);
  }

  /**
   * @returns void
   */
  ngOnDestroy(): void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }
}
